import React from 'react';
import { get, toInteger, isEmpty } from 'lodash';
import queryString from 'query-string';

// React view component to render
import { enrollToEpost as EnrollToEpostView } from 'ui/views/widgets/enrollToEpost';
import { EnrollToEpostService } from 'services/enrollToEpost';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { withRouter } from 'react-router-dom';
import { messageBus as MessageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import { getRedirectionRoute } from 'resolvers/callStateEntities';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import { getAppSyncData } from 'utils/getAppSyncData';
import { resolveMemberDetailSelection } from 'utils/memberDetailSelection';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { EnrollToEpost } from 'ui/containers/EnrollToEpost';
 *
 * // render
 * <EnrollToEpost propName='propValue'/>
 */
class _EnrollToEpost extends React.Component {
    
    constructor( props ) {
        super( props );

        // check if URL container `step` query parameter.
        const queryParams = queryString.parse( this.props.location.search );
        this.data = resolveMemberDetailSelection( this.props.location.state, {
            memberId: getAppSyncData( 'memberId' ),
            patientId: getAppSyncData( 'patientId' ),
            liveFallback: getAppSyncData( 'liveFallback' )
        } );
        const { patientId } = this.data;
        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            currentStep: toInteger( get( queryParams, 'step', 1 ) ),
            successView: undefined,
            patientId: patientId,
            origin: get( queryParams, 'origin', 1 )
        };

        // create service instance
        this.enrollToEpostService = new EnrollToEpostService();
        
        // start component data fetch
        this.fetchComponentData();

        this.__bind();

    }

    __bind() {
        this.handleSuccessfullEnrollment = this.handleSuccessfullEnrollment.bind( this );
        this.onSuccessfulEnrollment = this.onSuccessfulEnrollment.bind( this );
        MessageBus.on( MESSAGE_BUS_EVENTS.SHOW_EPOST_JOURNEY_SUCCESS_VIEW, this.onSuccessfulEnrollment, this );
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'Dashboard.fetchComponentData()' );

        // cancel ongoing request
        this.enrollToEpostService.cancel();

        // get status and service data
        const { status, data } = await this.enrollToEpostService.fetchComponentData( {
            url: this.props.widget.url,
            data: { context: this.data },
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    /**
     * show the success view
     */
    onSuccessfulEnrollment( { data } ) {
        log.debug( 'EnrollToEpost.onSuccessfulEnrollment()' );

        // A created patient is returned by the trusted enrollment session, never by indexed coverage.
        const createdPatientId = get( data, 'patientId' );
        if( SERVICE_RESPONSE_STATUS.FAILURE !== get( data, 'status' ) &&
            'string' === typeof createdPatientId && ! isEmpty( createdPatientId ) ) {
            this.data = { ...this.data, patientId: createdPatientId };
        }
        this.setState( { successView: data, patientId: this.data.patientId } );
    }

    /**
     * on successful enrollment
     */
    handleSuccessfullEnrollment( { action } ) {
        log.debug( 'EnrollToEpost.handleSuccessfullEnrollment()' );

        if( 'backToVerification' === action.name ) {

            const redirectionPath = getRedirectionRoute( this.props.callState );
            if( ! isEmpty( redirectionPath ) ) {
                this.props.history.push( redirectionPath );
            }
        }

        if( 'backToMemberDetail' === action.name ) {

            // switch to member details route
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                state: { ...this.data }
            } );
        }

       
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.enrollToEpostService.cancel(); // cancel ongoing requests
        MessageBus.off( MESSAGE_BUS_EVENTS.SHOW_EPOST_JOURNEY_SUCCESS_VIEW, this.onSuccessfulEnrollment, this );
    }

    /**
     * @desc Render a React view component
     */
    render() {


        return (
            <EnrollToEpostView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', undefined  ) }
                currentStep={ this.state.currentStep }
                requestPayload={ {
                    context: {
                        memberId: this.data.memberId,
                        patientId: this.data.patientId,
                        liveFallback: this.data.liveFallback,
                        journey: 'enroll',
                    }
                } }
                successView={ this.state.successView }
                onSuccessfullEnrollment = { this.handleSuccessfullEnrollment }
                origin={ this.state.origin }
            />
        );
    }
}

// set display name
_EnrollToEpost.displayName = '_EnrollToEpost';

// set default props
_EnrollToEpost.defaultProps = {};

// prop types
_EnrollToEpost.propTypes = {};

/*********************************************/

// with router functionality
export const EnrollToEpost = withRouter( _EnrollToEpost );
